import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from './components/Login';
import Scanner from './components/Scanner';
import SlotSelection from './components/SlotSelection';
import Results from './components/Results';
import Report from './components/Report';
import BankApp from './bank/BankApp';
import './App.css';
import { AvailableSlot } from './components/SlotSelection';

export interface ReservationData {
  id: number;
  code: string;
  location_code: string;
  location_id: number;
  customer_id: number;
  is_inps: number;
  rate_name: string;
  customer_name: string | null;
}

export interface ScanCheckResponse {
  reservation: ReservationData;
  available_slots: AvailableSlot[];
  slots_source: string;
}

// Legacy interface for compatibility (old single-slot response)
export interface GuestData {
  reservation: ReservationData;
  meal_eligibility: {
    eligible: boolean;
    discount_source: string;
    base_price: number;
    discount_percentage: number;
    discount_amount: number;
    final_price: number;
  };
  slot_info: {
    start_time: string;
    end_time: string;
    current_time: string;
    free_meals_limit: number;
    discounted_meals_limit: number;
  };
  processed_by: string;
  processed_at: string;
}

function App() {
  if (typeof window !== 'undefined' && window.location.hostname.startsWith('bank.')) {
    return <BankApp />;
  }

  return <FoodApp />;
}

function FoodApp() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [view, setView] = useState<'scanner' | 'slot-selection' | 'summary'>('scanner');
  const [reservationData, setReservationData] = useState<ReservationData | null>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);

  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleScanSuccess = (response: ScanCheckResponse) => {
    const { reservation, available_slots } = response;

    setReservationData(reservation);
    setAvailableSlots(available_slots);

    if (available_slots.length === 1) {
      // Skip selection - go directly to summary
      setSelectedSlot(available_slots[0]);
      setView('summary');
    } else if (available_slots.length > 1) {
      // Show selection screen
      setView('slot-selection');
    }
  };

  const handleSlotSelected = (slot: AvailableSlot) => {
    setSelectedSlot(slot);
    setView('summary');
  };

  const handleCancelSlotSelection = () => {
    // Reset to scanner
    setView('scanner');
    setReservationData(null);
    setAvailableSlots([]);
    setSelectedSlot(null);
  };

  const handleBackToScanner = () => {
    setView('scanner');
    setReservationData(null);
    setAvailableSlots([]);
    setSelectedSlot(null);
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                view === 'scanner' ? (
                  <Scanner onScanSuccess={handleScanSuccess} />
                ) : view === 'slot-selection' ? (
                  <SlotSelection
                    availableSlots={availableSlots}
                    customerName={reservationData?.customer_name || null}
                    onSelectSlot={handleSlotSelected}
                    onCancel={handleCancelSlotSelection}
                  />
                ) : (
                  <Results
                    reservationData={reservationData}
                    selectedSlot={selectedSlot}
                    onBackToScanner={handleBackToScanner}
                  />
                )
              ) : (
                <Login setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />
          <Route
            path="/report"
            element={
              isAuthenticated ? (
                <Report />
              ) : (
                <Login setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
